import {
  PageTemplate,
  PDFFeePortfolioDetail,
} from "@pages/platform-analysis/components/buttons/export-pdf/model";
import { FeeCalculatorPortfolioLevel } from "@models/platform-analysis/enums/fee/portfolioLevel";
import {
  MARGIN_DEFAULT,
  MAX_PAGE_INDEX_TO_SHOW_MAIN_HEADER,
  PAGE_BODY_HEIGHT,
  PAGE_BODY_HEIGHT_WITHOUT_HEADING,
  SUB_TITLE_HEIGHT,
} from "@pages/platform-analysis/components/buttons/export-pdf/constant";
import {
  getPortfolioDetails,
  getStringFamilyMemberAccounts,
} from "@pages/platform-analysis/util";
import { FeePortfolioDetailsConstants } from "@pages/platform-analysis/_id/steps/summary/constants";
import { PDFFeePortfolioDetailsSection } from "@pages/platform-analysis/components/buttons/export-pdf/components/sections/fee-portfolio-details/index";
import { pagesHandler } from "@pages/platform-analysis/components/buttons/export-pdf/util";
import { FeePortfolioDetailsConfigs as Configs } from "@pages/platform-analysis/components/buttons/export-pdf/components/sections/fee-portfolio-details/configs";
import { PDFTableRowProps } from "@pages/platform-analysis/components/buttons/export-pdf/components/layouts/tables/table-row";
import { FeeSubProductDTO } from "@models/platform-analysis/entities/steps/fee";

const processPortfolioDetailsData = (
  feePortfolioDetailData: PDFFeePortfolioDetail,
  isShowHoldingNumber?: boolean,
  portfolioSetup?: FeeCalculatorPortfolioLevel,
) => {
  let heightPortfolioDetails = SUB_TITLE_HEIGHT;

  const {
    transactionsOutside,
    transactionsWithin,
    investments,
    cfsBalances,
    expandBalances,
    northBalances,
  } = getPortfolioDetails(feePortfolioDetailData);

  feePortfolioDetailData?.familyMembers?.forEach((familyMember) => {
    heightPortfolioDetails += getFeePortfolioHeight(
      getStringFamilyMemberAccounts(familyMember.familyMemberAccounts),
    );
  });

  if (feePortfolioDetailData?.totalPortfolioValue) {
    heightPortfolioDetails += getFeePortfolioHeight(
      feePortfolioDetailData?.totalPortfolioValue.toString(),
    );
  }

  if (isShowHoldingNumber && feePortfolioDetailData?.totalDifferentInvestments)
    heightPortfolioDetails += getFeePortfolioHeight(
      feePortfolioDetailData?.totalDifferentInvestments.toString(),
    );

  if (
    investments?.length &&
    portfolioSetup === FeeCalculatorPortfolioLevel.No
  ) {
    heightPortfolioDetails += getFeePortfolioHeight(investments.join(", "));
  }

  if (transactionsOutside?.length) {
    heightPortfolioDetails += getFeePortfolioHeight(
      transactionsOutside.join(", "),
    );
  }

  if (transactionsWithin?.length) {
    heightPortfolioDetails += getFeePortfolioHeight(
      transactionsWithin.join(", "),
    );
  }

  const retailInsurancePolicyInvestments =
    investments?.filter((investment) =>
      investment.match(
        FeePortfolioDetailsConstants.RetailInsurancePoliciesRegex,
      ),
    ) ?? [];

  if (retailInsurancePolicyInvestments.length > 0) {
    heightPortfolioDetails += getFeePortfolioHeight(
      retailInsurancePolicyInvestments.join(", "),
    );
  }

  if (cfsBalances.length > 0) {
    heightPortfolioDetails += getFeePortfolioHeight(cfsBalances.join(", "));
  }

  if (expandBalances.length > 0) {
    heightPortfolioDetails += getFeePortfolioHeight(expandBalances.join(", "));
  }

  if (northBalances.length > 0) {
    heightPortfolioDetails += getFeePortfolioHeight(northBalances.join(", "));
  }

  return {
    name: "Portfolio details",
    occupyHeight: heightPortfolioDetails,
    dataSeparated: feePortfolioDetailData,
    hasContinue: false,
  };
};

const getFeePortfolioHeight = (feePortfolioStr: string) => {
  const numberOfLines = Math.ceil(
    feePortfolioStr.length / Configs.LimitOfCharsInOneLine,
  );

  return Configs.DefaultRowHeight * numberOfLines;
};

export const portfolioDetailPageHandler = ({
  pages,
  spaceAvailable,
  feePortfolioDetails,
  isShowHoldingNumber,
  portfolioSetup,
  hiddenSubProductIds,
  selectedSubProducts,
}: {
  pages: PageTemplate[];
  spaceAvailable: number;
  feePortfolioDetails?: PDFFeePortfolioDetail;
  isShowHoldingNumber?: boolean;
  portfolioSetup?: FeeCalculatorPortfolioLevel;
  hiddenSubProductIds?: string[];
  selectedSubProducts?: FeeSubProductDTO[];
}) => {
  if (!feePortfolioDetails) return spaceAvailable;

  const { occupyHeight } = processPortfolioDetailsData(
    feePortfolioDetails,
    isShowHoldingNumber,
  );
  const isIncludeMainHeader =
    pages.length <= MAX_PAGE_INDEX_TO_SHOW_MAIN_HEADER;
  const pageBodyHeight = isIncludeMainHeader
    ? PAGE_BODY_HEIGHT
    : PAGE_BODY_HEIGHT_WITHOUT_HEADING;
  const isAppend =
    spaceAvailable > pageBodyHeight / 2 &&
    spaceAvailable > occupyHeight &&
    spaceAvailable !== pageBodyHeight;

  const portfolioDetailsComp = (
    <PDFFeePortfolioDetailsSection
      isPlatformAnalysis
      data={feePortfolioDetails}
      isShowHoldingsNumber={isShowHoldingNumber}
      portfolioSetup={portfolioSetup}
      hiddenSubProductIds={hiddenSubProductIds}
      selectedSubProducts={selectedSubProducts}
    />
  );

  pagesHandler(pages, portfolioDetailsComp, isAppend);

  spaceAvailable = (isAppend ? spaceAvailable : pageBodyHeight) - occupyHeight;

  return spaceAvailable - MARGIN_DEFAULT;
};

export const getDynamicHeightText = (
  text: string,
  isPlatformAnalysis?: boolean,
): PDFTableRowProps["type"] =>
  isPlatformAnalysis
    ? undefined
    : text.length < Configs.LimitOfCharsInOneLine
    ? "DoubleLine"
    : "TripleLine";
